import { useTranslation } from 'react-i18next';
import { buildTeamProfileUrl } from '@/lib/inviteLinks';
import { platformLinkTarget } from '@/lib/platformEmbed';
import { Tooltip } from '@/components/Tooltip';

/**
 * Team badge — the user's cispracs.ru team. Inline in chat it renders the
 * compact tag (e.g. POLE); on the profile card it also renders the full team
 * name when the platform provides one. Renders nothing when the user has no
 * team. Data flows from the platform (SSO JWT + user-updated webhook).
 *
 * The tag is the team's slug on cispracs.ru, so by default it links to the
 * team's public page there. Pass `link={false}` where the badge already sits
 * inside another control (a member row that opens the user card) — a link
 * can't nest in a button.
 *
 * Styled as a quiet label rather than a filled accent chip: it's secondary
 * information next to a name, and a saturated chip on every line pulled the
 * eye away from the message itself.
 */
export function TeamTag({
  tag,
  name,
  className = '',
  link = true,
}: {
  tag?: string | null;
  name?: string | null;
  className?: string;
  link?: boolean;
}) {
  const { t } = useTranslation();
  if (!tag && !name) return null;
  const team = name || tag;
  const teamTitle = team ? t('members.team', { team }) : undefined;
  const chip =
    'studio-team-label shrink-0 rounded-[5px] bg-white/[0.07] px-[5px] py-px text-[11px] font-medium leading-4 tracking-normal text-ink-muted';
  const body = (
    <>
      {tag && <span className={chip + (link ? ' transition-colors group-hover/team:bg-white/[0.12] group-hover/team:text-ink' : '')}>{tag}</span>}
      {name && <span className="text-xs text-ink-muted">{name}</span>}
    </>
  );
  if (link && tag) {
    return (
      <Tooltip label={t('members.teamOnPlatform', { team })}>
      <a
        href={buildTeamProfileUrl(tag)}
        target={platformLinkTarget(buildTeamProfileUrl(tag))}
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={'group/team inline-flex items-center gap-1.5 rounded-[5px] outline-none focus-visible:ring-2 focus-visible:ring-accent/50 ' + className}
      >
        {body}
      </a>
      </Tooltip>
    );
  }
  return (
    <span className={'inline-flex items-center gap-1.5 ' + className} title={teamTitle} aria-label={teamTitle}>
      {body}
    </span>
  );
}
